import { FEAT_BOSSES, FEAT_GUARDIANS } from './feats.ts';
import type { GlyphId } from './glyphs.ts';
import type { EnemyKind } from './nightmares.ts';
import type { UniqueSlot } from './progression.ts';
import { UNIQUES, type UniqueId } from './saga.ts';
import type { ForkId } from './skills.ts';

/**
 * The forms of the unique pieces (docs/UNICAS.md §3): the awakening II opens two forms of the piece's power (A and B,
 * chosen in «lo que llevás», free and by mode) and the IV adds its final form, on top of the chosen one. Each one
 * changes one thing that is named and seen; the two of a piece are the same size (they change the role, not the
 * power). The numbers are starting ones, in fixed units and ticks (12 a second) like the rest of the fight.
 *
 * In a fight a hero carries each piece's form as bits (`Hero.uqForm`, parallel to `uq`): FORM_BIT.a or .b for the
 * chosen one, FORM_BIT.final from the stage IV, FORM_BIT.gaspar when the group heard Ser Gaspar (his guard comes in
 * person). The campaign works them out (`carried`): none without the piece's skill carried (or with the automaton as
 * a swarm), and a chosen form that clashes with the glyph or variant on that button is left out (§3, «no se suma»).
 */

export type FormKey = 'a' | 'b';

export interface FormDef {
  /** Its name in the texts (`uform.<id>`, `.desc`, `.up`, `.down`, `.when`). */
  id: string;
  fx: Record<string, number>;
  /** What it doesn't stack with on the same button: a glyph or a variant that does the same (docs/UNICAS.md §3). */
  clash?: { glyph?: GlyphId[]; fork?: ForkId[] };
}

/** A piece's forms: the saga's have two of the II and a final one; the panoply's only the final one (docs/PANOPLIA.md). */
export interface UniqueForms {
  a?: FormDef;
  b?: FormDef;
  final: FormDef;
}

export const FORM_BIT = { a: 1, b: 2, final: 4, gaspar: 8 } as const;

/** The stage that opens the two forms, and the one that brings the final form. */
export const FORM_STAGE = { choose: 2, final: 4 } as const;

const f = (id: string, fx: Record<string, number> = {}, clash?: FormDef['clash']): FormDef => ({ id, fx, ...(clash ? { clash } : {}) });

export const UNIQUE_FORMS: Record<UniqueId, UniqueForms> = {
  // ——— Weapons ———
  gateBlade: {
    /** What the bash hits and doesn't stun whole (elites, rams, the unstoppable) is thrown back `push`; bosses never. */
    a: f('wave', { push: 1000 }),
    /** The ground cracks `length` ahead of the blow; what crosses it in `ticks` is stunned `stunBp` of the bash's stun. */
    b: f('rift', { length: 3000, width: 450, ticks: 24, stunBp: 5000 }),
    /** Bosses are stunned too, `stun`, once every `every` each. */
    final: f('gateShut', { stun: 12, every: 120 }),
  },
  garrisonKey: {
    /** The beam slows whoever crosses it. */
    a: f('slowBeam', { slowBp: 4000, ticks: 12 }),
    /** Every `every` each beam arcs lightning to the `max` nearest within `radius` of its middle (breaks the chilled). */
    b: f('discharge', { every: 96, damage: 55, radius: 1800, max: 4 }),
    /** While three turrets stand joined they wear out at `rateBp` of the pace; one turret more. */
    final: f('holdFast', { extra: 1, rateBp: 5000 }),
  },
  fallenStaff: {
    /** Each leap leaves a line of fire from where the ball burst to its target, `ticks` long. */
    a: f('wake', { width: 450, ticks: 24, every: 6, damageBp: 1500 }, { glyph: ['trail'], fork: ['embers'] }),
    /** `chains` more leaps, each at `damageRelBp` of the piece's (its 80 % to 65 %, awakened alike). */
    b: f('prong', { chains: 2, damageRelBp: 8125 }),
    /** Each leap leaps once more, from its target to the nearest not yet struck. */
    final: f('masterRain', { range: 3000 }),
  },
  chapelLamp: {
    /** The second most hurt is healed too, `secondBp` of it. */
    a: f('highLamp', { secondBp: 5000 }),
    /** What the heal has left over stays as a shield, up to `capBp` of the ally's life, `ticks` long. */
    b: f('lightWard', { capBp: 1000, ticks: 96 }),
    /** Every `every`th blow is a beam through the line: it strikes what it crosses and heals each ally on it. */
    final: f('lucerna', { every: 5, length: 6000, width: 500, damageBp: 10000, healBp: 10000 }),
  },
  watchBow: {
    /** The mark takes `markAddBp` more (+30 % to +45 %, awakened on top) and lasts `markTicksBp` as long. */
    a: f('deepMark', { markAddBp: 1500, markTicksBp: 20000 }),
    /** Every `every`nd arrow goes through, at `damageRelBp` of the piece's (double to 150 %, awakened alike). */
    b: f('flurry', { every: 2, damageRelBp: 7500 }),
    /** The arrow that goes through pins the last it reaches: stunned `stun`, elites too (never bosses). */
    final: f('lance', { stun: 12 }),
  },
  wallAxe: {
    /** The whirl pulls from `radiusBp` as far, and for `steady` after nothing moves or throws the barbarian. */
    a: f('anchor', { radiusBp: 20000, steady: 24 }),
    /** Each one pulled heals `healBp` of the barbarian's life. */
    b: f('bloodSwirl', { healBp: 100 }),
    /** It pulls bosses too (`bossPull` at most) and what flies, and holds the flyers on the ground `ground`. */
    final: f('eye', { bossPull: 1000, ground: 24 }),
  },
  gasparHammer: {
    /** The smitten rings while stunned: what stands within `radius` of it is slowed. */
    a: f('toll', { radius: 1000, slowBp: 3000 }),
    /** The stun lasts `stun` more, and the stunned takes `holyBp` of the holy it is dealt. */
    b: f('heavy', { stun: 12, holyBp: 13000 }),
    /** A smite that kills calls a spectre of the old garrison for `ticks`: it strikes (`damageBp` of the smite) and taunts. */
    final: f('guard', { ticks: 48, damageBp: 6000, radius: 900, every: 12, taunt: 2500, hpBp: 3000 }),
  },
  oldRootStaff: {
    /** The held bleed: poison, `damageBp` of the roots' blow each `every`, `ticks` long (it readies a combustion). */
    a: f('bitterSap', { damageBp: 1500, every: 12, ticks: 48 }, { fork: ['strangle'] }),
    /** The roots hold `ticks` more, and elites `eliteBp` as long. */
    b: f('deepRoot', { ticks: 12, eliteBp: 20000 }, { glyph: ['prolong'] }),
    /** What dies held leaves a root (`radius`, `ticks`) that holds the next to step on it `hold`; `max` at once. */
    final: f('oldForest', { radius: 500, ticks: 240, hold: 24, max: 6 }),
  },
  // ——— Armours ———
  lastWatch: {
    /** While challenging, the allies within `radius` take `takenBp`. */
    a: f('parapet', { radius: 2000, takenBp: 7000 }),
    /** When the challenge ends it bursts around (`radius`) with `shareBp` of all it struck back. */
    b: f('vengeance', { radius: 2000, shareBp: 3000 }),
    /** About to fall while challenging: it stays at 1 and the challenge lasts `more` (once every `every`). */
    final: f('lastTurn', { more: 36, every: 720 }),
  },
  anvilHarness: {
    /** While the automaton stands within `radius`, the artificer takes `takenBp`. */
    a: f('ironGuard', { radius: 2000, takenBp: 8000 }),
    /** Its burst leaves the ground burning (`ticks`) and it is built again once. */
    b: f('forge', { ticks: 48, every: 6, damage: 12 }),
    /** The automaton no longer wears out: it lasts until it is broken. */
    final: f('living'),
  },
  frostMantle: {
    /** The ring is `radiusBp` as wide and freezes elites too, a third. */
    a: f('glacier', { radiusBp: 15000 }),
    /** Whatever strikes the mage while the ring stands is chilled (it readies the shatter). */
    b: f('counterFrost'),
    /** The ring also rises when `around` or more surround the mage, whatever its life (same rest). */
    final: f('winter', { around: 5, radius: 1600 }),
  },
  dwarfMail: {
    /** On the sanctuary nothing throws or holds the cleric or the allies. */
    a: f('firmStep'),
    /** The nightmares on the sanctuary are slowed. */
    b: f('atrium', { slowBp: 4000 }),
    /** Two sanctuaries: one stays where it was cast, the other walks with the cleric. */
    final: f('walkingGround'),
  },
  wandererCloak: {
    /** Where the ranger stood, its cloak stays `ticks`: the nightmares around (`taunt`) go for it. */
    a: f('emptyCloak', { ticks: 36, hpBp: 3000, taunt: 2500 }),
    /** A second net, at the nearest one behind the first (`range`). */
    b: f('doubleNet', { range: 2500 }, { glyph: ['echo'] }),
    /** After the leap, hidden `hidden`, and the first shot is a critical. */
    final: f('lostTrail', { hidden: 24 }),
  },
  boarHide: {
    /** The frenzy that starts by itself leaves the barbarian taking `takenBp` while it lasts. */
    a: f('coldBlood', { takenBp: 8000 }),
    /** As it starts, the barbarian charges the biggest nightmare within `range`: `damageBp` of its blow, a stun of `stun`. */
    b: f('boar', { range: 4000, damageBp: 20000, stun: 12 }),
    /** While any frenzy lasts the barbarian can't fall: it stays at 1. */
    final: f('unfallen'),
  },
  oathPlate: {
    /** When it breaks, the most hurt ally within `range` gets a new aegis of `shareBp` of what it held. */
    a: f('relay', { shareBp: 4000, range: 5000 }),
    /** When it breaks, those it taunts are stunned `stun` (elites a third). */
    b: f('steelEcho', { stun: 12 }),
    /** The aegis rises again by itself once, `after` it breaks. */
    final: f('steelOath', { after: 48 }),
  },
  dwarfBark: {
    /** In bear form the druid heals `healBp` of its life each second. */
    a: f('moss', { healBp: 100 }, { fork: ['wildHeart'] }),
    /** What strikes the bear is held `hold` (once every `every` each). */
    b: f('hug', { hold: 12, every: 48 }),
    /** When the bear form ends, a bear of bark stays and fights `ticks` by itself. */
    final: f('oldBear', { ticks: 96, hpBp: 4000, radius: 900, every: 12, taunt: 2000 }),
  },
  // ——— Amulets ———
  posternMedal: {
    /** The oath reaches the whole party, wherever they are. */
    a: f('allVow'),
    /** The oath keeps them up `durationBp` as long, and reaches `reachBp` as far. */
    b: f('longVow', { durationBp: 20000, reachBp: 5000 }, { glyph: ['prolong'] }),
    /** An ally falls with the oath ready: it goes off by itself (once every `every`). */
    final: f('medal', { every: 1080 }),
  },
  maelisKit: {
    /** The grenade leaves a puddle that heals (`healBp` of its heal each `every`, `ticks` long). */
    a: f('puddle', { ticks: 48, every: 12, healBp: 2500 }, { glyph: ['trail'] }),
    /** Two grenades a charge, `gap` apart, each at `powerBp`. */
    b: f('pair', { gap: 1200, powerBp: 6000 }, { glyph: ['echo', 'fan'] }),
    /** Every `every`rd grenade splits in three, `gap` around the point. */
    final: f('flasks', { every: 3, gap: 900 }, { glyph: ['fan'] }),
  },
  stormEye: {
    /** When the mage hasn't moved for `still`, the storm strikes `damageBp` as hard. */
    a: f('stillEye', { still: 24, damageBp: 14000 }),
    /** Each pulse leaps to one more beyond it, within `range`. */
    b: f('leapBolt', { range: 2500 }),
    /** Two storms: one follows the mage, the other stays where it was cast. */
    final: f('twoStorms'),
  },
  healerRosary: {
    /** Those it raises get an aegis of `shieldBp` of their life, `ticks` long. */
    a: f('guardingDawn', { shieldBp: 2000, ticks: 96 }, { fork: ['dawnRebirth'] }),
    /** It recharges at `cooldownBp`, but reaches only the allies within `reach`, not the whole party. */
    b: f('earlyDawn', { cooldownBp: 7500, reach: 6000 }, { glyph: ['celerity'] }),
    /** Whoever falls in the `ticks` after a dawn gets up by itself, at `hpBp`. */
    final: f('secondDawn', { ticks: 120, hpBp: 5000 }),
  },
  mossWhistle: {
    /** What steps under the rain is pinned `stun` as it enters (once a rain each). */
    a: f('spikes', { stun: 12 }, { fork: ['pinningRain'] }),
    /** The rain follows the biggest nightmare under it. */
    b: f('followRain'),
    /** Where the rain has killed `kills`, a second one falls by itself, `radiusBp` as wide and `ticksBp` as long. */
    final: f('closedSky', { kills: 5, radiusBp: 6000, ticksBp: 5000 }),
  },
  tailFang: {
    /** Each leap in a chain strikes `stepBp` harder than the one before (up to `max` steps). */
    a: f('crescendo', { stepBp: 2500, max: 6 }),
    /** A leap in a chain heals `healBp` as much. */
    b: f('hunger', { healBp: 20000 }),
    /** A leap is ready again also when it struck `hits` or more without killing. */
    final: f('endless', { hits: 3 }),
  },
  vigilCandle: {
    /** When it ends, the hallowed ground leaps to the most beset ally within `range` and lasts `ticks` more there. */
    a: f('leapFlame', { ticks: 36, range: 6000, beset: 2500 }),
    /** The inked nightmares on it strike at `blindBp`. */
    b: f('blindFlame', { blindBp: 8000 }),
    /** When it ends it leaves a circle `ticks` long that no nightmare enters (bosses do). */
    final: f('vigil', { ticks: 72 }),
  },
  maelisSeed: {
    /** What steps into the bloom is held `hold` (once every `every` each). */
    a: f('tangle', { hold: 12, every: 72 }, { fork: ['briars'] }),
    /** The bloom chills the nightmares it touches (it readies the shatter). */
    b: f('pollen', {}, { glyph: ['rime'] }),
    /** When it ends it leaves `count` sprouts (`radius`, `gap` around) that heal `healBp` of the bloom's by themselves, `ticks` long. */
    final: f('garden', { count: 3, gap: 900, radius: 900, ticks: 120, healBp: 5000 }),
  },

  // ——— The panoply: only a final form each (docs/PANOPLIA.md §10) ———
  /** The first basic attack of each hero on an exposed one is a critical. */
  butcherCleaver: { final: f('noGuard') },
  /** The slam goes again `after` ticks later, `damageBp` as hard. */
  barracksMace: { final: f('twoBlows', { after: 6, damageBp: 5000 }, { glyph: ['echo'] }) },
  /** The chain drags elites too, half the way (bosses never). */
  wellHook: { final: f('haul') },
  /** After the swap the warrior takes `takenBp` for `ticks`. */
  drillPlate: { final: f('steady', { takenBp: 5000, ticks: 36 }) },
  /** The wall's push reaches `reach` and slows what it pushes `slowBp` for `ticks`. */
  frontRankCuirass: { final: f('wallAdvance', { reach: 2000, slowBp: 3000, ticks: 24 }) },
  /** When the shout ends, the challenged still standing hit at `bp` for `ticks`. */
  criersCuirass: { final: f('quail', { bp: 5000, ticks: 24 }) },
  /** The staff comes back to the hand: the banner recharges at `cooldownBp`. */
  menderThimble: { final: f('staffBack', { cooldownBp: 5000 }) },
  /** Each ring mends the allies it reaches `healBp` of their life. */
  bellNail: { final: f('rallyCall', { healBp: 300 }) },
  /** The burst stuns `stun` (a piece's stun). */
  veteranMedallion: { final: f('lastBlow', { stun: 12 }) },
  /** The hive lasts `ticksBp` as long and its wasps slow `slowBp` for `slowTicks`. */
  hiveStaff: { final: f('queen', { ticksBp: 20000, slowBp: 2000, slowTicks: 24 }) },
  /** Where the cloud ends, mushrooms let it out once more, `damageBp` as hard. */
  herbalistStaff: { final: f('mushrooms', { damageBp: 5000 }) },
  /** The lash leaves `n` hexes of briars across its way, `ahead` in front, `ticks` long, slowing `slowBp`. */
  briarStaff: { final: f('hedge', { n: 3, ahead: 1500, ticks: 36, slowBp: 4000 }, { glyph: ['trail'] }) },
  /** When the root ends it leaves a sprout that mends `healBp` of each ally's life within `radius` every `every`, `ticks` long. */
  poulticeTunic: { final: f('sprout', { ticks: 48, radius: 1500, every: 12, healBp: 200 }) },
  /** Taking wolf form stuns `stun` around (`radius`; a piece's stun). */
  goatherdPelt: { final: f('howl', { stun: 24, radius: 2000 }) },
  /** What the thorns prick is poisoned `ticks` (every `every`, the wall's damage), readying the burst of fire. */
  gardenerPlate: { final: f('venomThorns', { every: 6, ticks: 24 }) },
  /** What the plague poisoned hits at `bp` while it stays poisoned (elites and bosses at half of it). */
  undertakerPendant: { final: f('blackCloud', { bp: 8000, ticks: 6 }) },
  /** What stands on the broken ground takes `takenBp` (sin defensa). */
  groundStone: { final: f('unforgiving', { takenBp: 11500 }) },
  /** The trees follow the druid. */
  orchardAcorn: { final: f('walkingTrees') },
  // The barbarian's.
  /** Two axes, at two targets. */
  shepherdAxe: { final: f('axePair') },
  /** The kick throws elites too, at `pushBp` of the distance. */
  brawlerAxe: { final: f('bigKick', { pushBp: 5000 }) },
  /** Below `belowBp` of its life, the brutal blow strikes everything within `radius` of its target too. */
  carterAxe: { final: f('carterEdge', { belowBp: 2000, radius: 1000 }) },
  /** The charge's path stays broken `ticks` long, slowing `slowBp`. */
  bullTusk: { final: f('dustTrail', { slowBp: 4000, ticks: 36 }, { glyph: ['trail'] }) },
  /** The shout's stunned hit at `bp` for `ticks` after they get up. */
  wolfSkull: { final: f('panic', { bp: 7000, ticks: 36 }) },
  /** The bleeding go `slowBp` slower. */
  tannerFang: { final: f('heavyBlood', { slowBp: 2000 }) },
  /** The allies the roar reaches attack faster while it lasts (`attackSpeedBp`; with the war cry's and the inspiration's, the stronger counts). */
  foremanPelt: { final: f('warDrum', { attackSpeedBp: 11500 }) },
  /** While the duel lasts, the other nightmares strike the barbarian at `takenBp`. */
  challengerHide: { final: f('arena', { takenBp: 5000 }) },
  /** Each spirit that kills mends the barbarian `healBp` of its life. */
  ancestorPelts: { final: f('healingSpirits', { healBp: 200 }) },
  // The paladin's.
  /** The blinded can't strike from afar while it lasts. */
  firstRoundHammer: { final: f('hush') },
  /** The atoning blow strikes everything within `radius` of its target too. */
  penitenceHammer: { final: f('absolution', { radius: 900 }) },
  /** The seal marks two. */
  keeperHammer: { final: f('twoSeals') },
  /** Two loose hammers, flying out both ways. */
  armsMasterCuirass: { final: f('twinHammers') },
  /** Each blessed blow mends the one who strikes `healBp` of its life. */
  armourerCoat: { final: f('holyArm', { healBp: 200 }) },
  /** An ally below `belowBp` recharges the intervention, once every `every` ticks. */
  stairPlates: { final: f('alwaysThere', { belowBp: 2000, every: 180 }) },
  /** What the rampart covers doesn't go below 1 while it lasts. */
  blessedStone: { final: f('unyielding') },
  /** About to fall during the sacrifice, the paladin stays at 1 and the sacrifice ends. */
  elderCharm: { final: f('staying') },
  /** Each inked nightmare the paladin fells lengthens the avatar `extra` ticks, up to twice its length. */
  prayerBead: { final: f('unfading', { extra: 12 }) },
  // The ranger's.
  /** The volley's arrows that had no target fall again in the area `after` ticks later. */
  hunterBow: { final: f('secondPass', { after: 6 }) },
  /** What the cloud poisons hits at `bp` for `ticks` (pegan menos). */
  trackerBow: { final: f('fever', { bp: 8000, ticks: 72 }) },
  /** Leaping below `belowBp` of life, the disengage recharges at `cdBp`. */
  pantryBow: { final: f('timelyLeap', { belowBp: 3000, cdBp: 5000 }) },
  /** The arrow turns twice. */
  quarryBow: { final: f('twoTurns') },
  /** The prey dead, the hawk marks the biggest nearby at once. */
  falconerJerkin: { final: f('hawkReturns') },
  /** The ranger's basic attacks crit `critBp` of the time on what the flare brought down. */
  signalerCoat: { final: f('burntAir', { critBp: 2500 }) },
  /** Shutting, the snare drags those within `radius` toward its catch, `pull` far. */
  trapperJerkin: { final: f('draggingSnare', { radius: 2000, pull: 900 }) },
  /** The titan's arrow comes back down its line `after` ticks later, `damageBp` as hard. */
  ballistaTip: { final: f('returningArrow', { after: 8, damageBp: 5000 }) },
  /** The hiding over, the next basic attack strikes `mulBp`. */
  archerFeather: { final: f('lastShot', { mulBp: 30000 }) },
  /** For `ticks` after each of the ranger's criticals, the allies' basic attacks on that target crit `critBp` of the time. */
  tunerLens: { final: f('guidingEye', { ticks: 24, critBp: 2500 }) },
  // The cleric's.
  /** The inspiration reaches the allies within `radius` of the mended too. */
  nurseStaff: { final: f('heart', { radius: 2000 }, { fork: ['mendChain'] }) },
  /** A second ray across the first, through its middle. */
  watercarrierStaff: { final: f('twoRays') },
  /** Each jump also takes a bad state off the healed. */
  lamplighterStaff: { final: f('cleans') },
  /** The renewal over, it passes to the most hurt ally within `range` for `bp` of its time. */
  midwifeStaff: { final: f('passingRenew', { bp: 5000, range: 3000 }, { fork: ['sharedRenew'] }) },
  /** A second totem, by the next most surrounded ally. */
  gardenerHabit: { final: f('twoTotems', {}, { glyph: ['pack'] }) },
  /** The cleanse leaves hallowed ground `ticks`, healing `healBp` of a sanctuary's. */
  cisternStole: { final: f('holyWater', { ticks: 48, healBp: 5000 }, { glyph: ['trail'] }) },
  /** The prayer comes again `after` ticks later, at `bp`. */
  pilgrimSmock: { final: f('amen', { after: 48, bp: 3000 }, { glyph: ['echo'] }) },
  /** Where the column ends it leaves hallowed ground `ticks`. */
  bronzeSun: { final: f('dawnStays', { ticks: 36 }, { glyph: ['trail'] }) },
  /** The choir over, it raises a fallen ally within `radius` at `hpBp` of its life. */
  infirmaryBell: { final: f('lastSong', { radius: 3000, hpBp: 3000 }) },
  /** Raised, the marked one passes the mark to the most hurt ally, once. */
  vigilMedal: { final: f('secondMark') },
  // The artificer's.
  /** `extra` mines more at once. */
  minerPick: { final: f('minefield', { extra: 2 }) },
  /** What dies burning bursts in fire around (`radius`), `damageBp` of the jet's blow, with no chain. */
  stokerPoker: { final: f('emberBurst', { radius: 1000, damageBp: 5000 }) },
  /** What the wrench mended doesn't drop below 1 for `ticks`. */
  buildersSquare: { final: f('rivetHold', { ticks: 36 }) },
  /** Two drones go out. */
  clockmakerGear: { final: f('secondDrone', {}, { glyph: ['pack'] }) },
  /** When it ends it bursts: `damage` and a stun of `stun` (a piece's) within `radius` of each plate. */
  locksmithRivet: { final: f('plateBurst', { radius: 1000, stun: 12, damage: 40 }) },
  /** The coil walks with the artificer. */
  rodmanCopper: { final: f('shoulderCoil') },
  /** When it ends, another volley at `damageBp`. */
  gunnerPlate: { final: f('secondVolley', { damageBp: 5000 }, { glyph: ['echo'] }) },
  /** When it ends what was in it (allies and buildings) gets a shield of `shieldBp` of its life, `ticks` long. */
  masonApron: { final: f('limeAndStone', { shieldBp: 1000, ticks: 72 }) },
  /** The hound goes first for the nearest elite and draws it `ticks` long. */
  kennelCoat: { final: f('scent', { ticks: 36 }) },
  // The mage's.
  /** At the first one the chain splits in two. */
  stormStaff: { final: f('splitBolt') },
  /** If the eaten one dies while the acid lasts the dart flies again to the nearest within `range` (`gens` at most). */
  distillerStaff: { final: f('seekingDart', { range: 3000, gens: 3 }) },
  /** A shatter on the frozen line reaches everything on it (a shard: frost, it doesn't shatter again). */
  pantryStaff: { final: f('icicle') },
  /** The burst leaves another wall, `lenBp` as long, `ticks` long. */
  whiteWallRobe: { final: f('wallAgain', { lenBp: 5000, ticks: 24 }) },
  /** It grows `stepBp` with each nightmare it touches, up to `maxBp`. */
  teacherRobe: { final: f('growingOrb', { stepBp: 1000, maxBp: 20000 }) },
  /** The chain of a bound one that dies passes to the nearest nightmare within `range`. */
  jailerSurcoat: { final: f('seekingChains', { range: 3000 }) },
  /** Where the mage was an image stands `ticks` long and draws the nightmares within `taunt`. */
  messengerCape: { final: f('mirrorImage', { ticks: 36, taunt: 2500 }) },
  /** Each nightmare that dies inside makes it last `ticks` more, up to `maxBp` of its length. */
  iceTear: { final: f('feedingStorm', { ticks: 6, maxBp: 20000 }) },
  /** The first blow on one the open rift stunned hits `bp` as hard. */
  dreamStone: { final: f('dreamBlow', { bp: 20000 }) },
  /** The last meteor is huge: `hits` blows, `every` ticks apart, over `blastBp` of the blast. */
  cometEmber: { final: f('cometTail', { hits: 5, every: 3, blastBp: 25000 }, { fork: ['greatMeteors'] }) },
};

/**
 * What a piece's own power doesn't stack with on its button (docs/PANOPLIA.md §8, «no se suma»): with that variant or
 * glyph carried there the power stays out of the fight (the piece gives its slot's attributes and effect), and the
 * screen says why, as with the forms.
 */
export const PIECE_CLASH: Partial<Record<UniqueId, { glyph?: GlyphId[]; fork?: ForkId[] }>> = {
  barracksMace: { fork: ['hardSlam'] },
  bellNail: { fork: ['longEcho'], glyph: ['echo'] },
  shepherdAxe: { fork: ['cleavingAxe'] },
  foremanPelt: { fork: ['longRoar'], glyph: ['echo'] },
  challengerHide: { fork: ['longDuel'] },
  firstRoundHammer: { fork: ['wideLight'] },
  armourerCoat: { glyph: ['bond'] },
  blessedStone: { fork: ['sacredRampart'] },
  archerFeather: { fork: ['longHidden'] },
  watercarrierStaff: { glyph: ['trail'] },
  infirmaryBell: { fork: ['longChoir'] },
  minerPick: { fork: ['clusterMine'] },
  gunnerPlate: { fork: ['incendiaryMortar'], glyph: ['trail'] },
  stormStaff: { fork: ['longArc'] },
  pantryStaff: { glyph: ['trail'] },
  teacherRobe: { fork: ['deepOrb'] },
};

/** Whether a piece's own power clashes with what its button carries (it stays out). */
export const pieceClash = (id: UniqueId, fork: ForkId | null, glyph: GlyphId | null): boolean => {
  const c = PIECE_CLASH[id];
  return !!c && ((!!fork && !!c.fork?.includes(fork)) || (!!glyph && !!c.glyph?.includes(glyph)));
};

/** Every form's text id, A, B and final, in piece order (texts, tests). */
export const FORM_IDS: readonly string[] = (Object.keys(UNIQUE_FORMS).sort() as UniqueId[]).flatMap((id) => [UNIQUE_FORMS[id].a?.id, UNIQUE_FORMS[id].b?.id, UNIQUE_FORMS[id].final.id].filter((x): x is string => !!x));

/** A form's numbers by its piece and key. */
export const formFx = (id: UniqueId, key: FormKey | 'final'): Record<string, number> => UNIQUE_FORMS[id][key]?.fx ?? {};

/** Whether a chosen form clashes with what the button carries: the glyph or the variant doing the same (it is left out). */
export const formClash = (id: UniqueId, key: FormKey | 'final', fork: ForkId | null, glyph: GlyphId | null): boolean => {
  const c = UNIQUE_FORMS[id][key]?.clash;
  if (!c) return false;
  return (!!fork && !!c.fork?.includes(fork)) || (!!glyph && !!c.glyph?.includes(glyph));
};

/** The pieces whose power has no button of its own (it always goes): their forms are chosen by the passive. */
export const FREE_POWER: readonly UniqueId[] = (Object.keys(UNIQUES).sort() as UniqueId[]).filter((id) => UNIQUES[id].skill === null);

/** The slot of a piece, for the preparation (one unique per class and slot). */
export const formSlotOf = (id: UniqueId): UniqueSlot => UNIQUES[id].slot;

/**
 * What is a boss for the forms (docs/UNICAS.md §3, «Qué es un jefe»: Onda, Portón cerrado, Ojo del huracán, Velada):
 * the Legend's (feats), the Warden, the magma lord, the Chasm's guardians, the witch, the shell, the sagas' bosses and
 * the dragon. One list: the sim adds whatever its fight marks as its boss (the act's, the crypt's) and the Unsleeping.
 * The old `isBoss` (only the witch and the shell) is too short for them.
 */
export const UNIQUE_BOSSES: readonly EnemyKind[] = [...new Set<EnemyKind>([...FEAT_BOSSES, ...FEAT_GUARDIANS, 'uro', 'drinker', 'prestado', 'ragna', 'bruni', 'corvin'])];
